import { createContext } from 'preact';
import { useContext, useEffect, useRef, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { peek } from '../api.js';
import { h } from '../format.js';
import { navigate } from '../router.js';
import { Icon } from '../shell.js';
import { FoldHead, useFold } from '../fold.js';
import { DICT, texts, useLang, type Lang } from '../i18n.js';
import { onPlanChanged, openPlan, PT } from '../planTime.js';
import { timerChanged } from '../timer.js';
import { toast } from '../ui.js';
import type { Api as ApiT } from '../api.js';

/** GET /api/my-week (UX-1003): mirrors apps/api/src/services/myWeek.ts. */
export type WeekItem =
  | { kind: 'busy'; from: string; to: string; allDay: boolean; title?: string; event?: BusyEvent }
  | { kind: 'work'; title: string; sub: string; hours: number; projectId: string | null }
  | { kind: 'shoot'; title: string; hours: number; projectId: string | null; booked?: BookedShoot }
  | { kind: 'due'; title: string; sub: string; projectId: string }
  | { kind: 'away'; part: 'full' | 'am' | 'pm'; requested: boolean }
  | { kind: 'plan'; id: string; title: string; sub: string; from: string; to: string; hours: number; projectId: string; videoId: string | null; note: string; google: 'off' | 'sent' | 'error' };
/** The person's own Google event: what its card shows (read live, never stored). */
export interface BusyEvent {
  link?: string;
  location?: string;
  meet?: string;
  guests?: { name: string; response: 'yes' | 'no' | 'maybe' | 'pending'; organizer: boolean; self: boolean }[];
}
/** A shoot booked in the Hub. */
export interface BookedShoot {
  from: string;
  to: string;
  location: string;
  plan: string;
  crew: string[];
  contact: { name: string; phone: string | null } | null;
  invite: boolean;
}
export interface WeekDay {
  date: string;
  today: boolean;
  closed: boolean;
  holiday: boolean;
  plannedH: number;
  over: boolean;
  items: WeekItem[];
}
export interface MyWeekView {
  week: string;
  weekNo: number;
  today: string;
  thisWeek: string;
  planned: { hours: number; capacityH: number | null } | null;
  calendar: 'ok' | 'off' | 'error';
  /** "+ Planera" (Projects on) and whether the block can go to the person's Google Calendar; older APIs leave it out */
  plan?: { on: boolean; google: boolean };
  days: WeekDay[];
}

/** Items a day shows before "+n more" (keeps the calendar about 260 px high). */
export const FOLD = 4;

const dt = (k: string) => new Date(`${k}T12:00:00Z`);
const addDays = (k: string, n: number) => new Date(dt(k).getTime() + n * 86400_000).toISOString().slice(0, 10);
const dayNo = (k: string) => +k.slice(8, 10);

/** "Week 42 · 12–18 Oct", across months "Week 44 · 26 Oct – 1 Nov" ("Vecka 42 · 12–18 okt"). */
export function weekTitle(v: Pick<MyWeekView, 'week' | 'weekNo'>, lang: Lang = 'en'): string {
  const t = DICT[lang].week;
  const end = addDays(v.week, 6);
  const m1 = t.months[+v.week.slice(5, 7) - 1];
  const m2 = t.months[+end.slice(5, 7) - 1];
  return t.title(v.weekNo, m1 === m2 ? `${dayNo(v.week)}–${dayNo(end)} ${m2}` : `${dayNo(v.week)} ${m1} – ${dayNo(end)} ${m2}`);
}

/**
 * "Planned 34 h": the plan, not a verdict. Capacity and overload stay with the producers in Team, who can re-plan;
 * Monday's hours are package estimates, so a score here would mostly worry people (owner, 2026-10-15).
 */
export function plannedLine(p: MyWeekView['planned'], lang: Lang = 'en'): string | null {
  return p ? DICT[lang].week.plannedH(h(p.hours)) : null;
}

/** One line of text per item; colour is never the only signal. */
export function itemText(it: WeekItem, lang: Lang = 'en'): { main: string; sub?: string } {
  const t = DICT[lang].week;
  switch (it.kind) {
    case 'busy':
      // your own event's title when the Hub may read it, otherwise just the busy time
      if (it.title) return { main: it.title, sub: it.allDay ? t.allDay : `${it.from}–${it.to}` };
      return { main: it.allDay ? t.busyAllDay : t.busy(it.from, it.to) };
    case 'away':
      return { main: `${t.away}${it.part === 'am' ? ` · ${t.morning}` : it.part === 'pm' ? ` · ${t.afternoon}` : ''}${it.requested ? ` · ${t.requested}` : ''}` };
    case 'due':
      return { main: `${t.due} · ${it.title}`, sub: it.sub };
    case 'shoot':
      return { main: `${t.shoot} · ${it.title}`, sub: it.booked ? `${it.booked.from}–${it.booked.to}${it.booked.location ? ` · ${it.booked.location}` : ''}` : h(it.hours) };
    case 'work':
      return { main: it.title, sub: `${it.sub} · ${h(it.hours)}` };
    case 'plan':
      return { main: it.title, sub: `${it.from}–${it.to} · ${it.sub}` };
  }
}

const go = (path: string) => (e: Event) => (e.preventDefault(), navigate(path));
/** On the freelancer's link there are no staff project pages: the page says where a project opens instead. */
const OpenProject = createContext<((projectId: string) => void) | null>(null);
const useGo = () => {
  const open = useContext(OpenProject);
  return (projectId: string, path: string) => (open ? (e: Event) => (e.preventDefault(), open(projectId)) : go(path));
};

/** Own Google events with a title, and shoots booked in the Hub, open a card. */
const opens = (it: WeekItem) => (it.kind === 'busy' && Boolean(it.title)) || (it.kind === 'shoot' && Boolean(it.booked));

/** The day's kind of work at a glance (owner 2026-10-20): a camera on shoots, scissors on draft deadlines; the words stay. */
export function KindIcon(p: { kind: 'shoot' | 'edit' }) {
  return (
    <span class={`role-ico role-ico--sm role-ico--${p.kind === 'shoot' ? 'camera' : 'editor'}`} aria-hidden="true">
      <Icon name={p.kind === 'shoot' ? 'video' : 'scissors'} />
    </span>
  );
}

/** A planned block (owner 2026-11-03): the name opens it to move or remove, ▶ starts the timer on its project. */
function PlanItem(p: { it: Extract<WeekItem, { kind: 'plan' }>; date: string; api: ApiT }) {
  const lang = useLang();
  const t = itemText(p.it, lang);
  const c = CT[lang];
  const it = p.it;
  const start = async () => {
    try {
      await p.api.post('/api/time/timer/start', { projectId: it.projectId, ...(it.videoId ? { videoId: it.videoId } : {}) });
      timerChanged();
      toast({ text: c.timerOn(it.title) });
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
    }
  };
  return (
    <li class="wkc-it wkc-it--plan">
      <button type="button" class="wkc-main" title={`${PT[lang].editTitle}: ${t.main}`} onClick={() => openPlan({ block: { id: it.id, projectId: it.projectId, videoId: it.videoId, day: p.date, start: it.from, end: it.to, note: it.note, google: it.google } })}>
        {t.main}
      </button>
      <span class="wkc-sub" title={[t.sub, it.note].filter(Boolean).join(' · ')}>
        {t.sub}
      </span>
      <button type="button" class="wkc-play" aria-label={c.startTimer(it.title)} title={c.startTimer(it.title)} onClick={() => void start()}>
        <Icon name="play" />
      </button>
    </li>
  );
}

function Item(p: { it: WeekItem; open: boolean; onOpen: (el: HTMLElement) => void }) {
  const t = itemText(p.it, useLang());
  const pid = 'projectId' in p.it ? p.it.projectId : null;
  const goTo = useGo();
  const icon = p.it.kind === 'shoot' ? <KindIcon kind="shoot" /> : p.it.kind === 'due' ? <KindIcon kind="edit" /> : null;
  return (
    <li class={`wkc-it wkc-it--${p.it.kind}${p.it.kind === 'away' && p.it.requested ? ' is-req' : ''}${p.open ? ' is-open' : ''}${icon ? ' has-ico' : ''}`}>
      {icon}
      {opens(p.it) ? (
        <button type="button" class="wkc-main" title={t.main} aria-expanded={p.open} aria-haspopup="dialog" onClick={(e) => p.onOpen(e.currentTarget as HTMLElement)}>
          {t.main}
        </button>
      ) : pid ? (
        <a class="wkc-main" href={`/projects/${pid}`} onClick={goTo(pid, `/projects/${pid}`)} title={t.main}>
          {t.main}
        </a>
      ) : (
        <span class="wkc-main" title={t.main}>
          {t.main}
        </span>
      )}
      {t.sub ? (
        <span class="wkc-sub" title={t.sub}>
          {t.sub}
        </span>
      ) : null}
    </li>
  );
}

const CT = texts(
  {
    google: 'Google Calendar',
    hubShoot: 'Shoot · booked in the Hub',
    when: 'When',
    where: 'Where',
    guests: 'Guests',
    crew: 'Crew',
    contact: 'Contact',
    plan: 'Plan',
    you: 'You',
    organizer: 'organizer',
    answers: (yes: number, pending: number, no: number) => [yes ? `${yes} yes` : '', pending ? `${pending} waiting` : '', no ? `${no} no` : ''].filter(Boolean).join(', '),
    meet: 'Join Meet',
    openGoogle: 'Open in Google Calendar',
    openProject: 'Open project',
    inGoogle: 'The invite is in your Google Calendar.',
    close: 'Close',
    startTimer: (what: string) => `Start the timer on ${what}`,
    timerOn: (what: string) => `Timer started on ${what}`,
    addPlan: '+ Plan',
    planOn: (day: string) => `Plan time on ${day}`
  },
  {
    google: 'Google Kalender',
    hubShoot: 'Inspelning · bokad i Hubben',
    when: 'När',
    where: 'Var',
    guests: 'Gäster',
    crew: 'Crew',
    contact: 'Kontakt',
    plan: 'Plan',
    you: 'Du',
    organizer: 'arrangör',
    answers: (yes: number, pending: number, no: number) => [yes ? `${yes} ja` : '', pending ? `${pending} väntar` : '', no ? `${no} nej` : ''].filter(Boolean).join(', '),
    meet: 'Gå med i Meet',
    openGoogle: 'Öppna i Google Kalender',
    openProject: 'Öppna projektet',
    inGoogle: 'Inbjudan finns i din Google Kalender.',
    close: 'Stäng',
    startTimer: (what: string) => `Starta timern på ${what}`,
    timerOn: (what: string) => `Timern startad på ${what}`,
    addPlan: '+ Planera',
    planOn: (day: string) => `Planera tid ${day}`
  }
);

/** The card a clicked event opens: details, and links to Meet, Google Calendar or the project. */
function EventCard(p: { it: WeekItem; date: string; at: DOMRect; onClose: () => void }) {
  const lang = useLang();
  const c = CT[lang];
  const tw = DICT[lang].week;
  const goTo = useGo();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: p.at.bottom + 6, left: p.at.left });
  useEffect(() => {
    const box = ref.current!;
    const w = box.offsetWidth;
    const hgt = box.offsetHeight;
    const left = Math.max(16, Math.min(p.at.left, innerWidth - w - 16));
    const below = p.at.bottom + 6;
    const top = below + hgt > innerHeight - 8 && p.at.top - hgt - 6 > 8 ? p.at.top - hgt - 6 : Math.max(8, Math.min(below, innerHeight - hgt - 8));
    setPos({ top, left });
    box.focus();
    const key = (e: KeyboardEvent) => e.key === 'Escape' && p.onClose();
    const click = (e: MouseEvent) => !box.contains(e.target as Node) && p.onClose();
    const away = () => p.onClose();
    addEventListener('keydown', key);
    setTimeout(() => addEventListener('mousedown', click), 0);
    addEventListener('scroll', away, true);
    addEventListener('resize', away);
    return () => {
      removeEventListener('keydown', key);
      removeEventListener('mousedown', click);
      removeEventListener('scroll', away, true);
      removeEventListener('resize', away);
    };
  }, [p.at]);
  const day = `${tw.dow[dt(p.date).getUTCDay()]} ${dayNo(p.date)} ${tw.months[+p.date.slice(5, 7) - 1]}`;
  const it = p.it;
  let body;
  if (it.kind === 'busy') {
    const e = it.event ?? {};
    const g = e.guests ?? [];
    const n = (r: string) => g.filter((x) => x.response === r).length;
    body = (
      <>
        <span class="wkc-card-tag">{c.google}</span>
        <h3>{it.title}</h3>
        <dl>
          <dt>{c.when}</dt>
          <dd>{it.allDay ? `${day} · ${tw.allDay}` : `${day} · ${it.from}–${it.to}`}</dd>
          {e.location ? (
            <>
              <dt>{c.where}</dt>
              <dd>{e.location}</dd>
            </>
          ) : null}
          {g.length ? (
            <>
              <dt>{c.guests}</dt>
              <dd>
                {g.map((x) => `${x.self ? c.you : x.name}${x.organizer ? ` (${c.organizer})` : ''}`).join(', ')}
                {g.length > 1 ? ` · ${c.answers(n('yes'), n('pending') + n('maybe'), n('no'))}` : ''}
              </dd>
            </>
          ) : null}
        </dl>
        {e.meet || e.link ? (
          <div class="cluster">
            {e.meet ? (
              <a class="btn btn-sm btn-primary" href={e.meet} target="_blank" rel="noopener">
                {c.meet}
              </a>
            ) : null}
            {e.link ? (
              <a class="btn btn-sm" href={e.link} target="_blank" rel="noopener">
                {c.openGoogle} ↗
              </a>
            ) : null}
          </div>
        ) : null}
      </>
    );
  } else if (it.kind === 'shoot' && it.booked) {
    const b = it.booked;
    body = (
      <>
        <span class="wkc-card-tag">{c.hubShoot}</span>
        <h3>{it.title}</h3>
        <dl>
          <dt>{c.when}</dt>
          <dd>{`${day} · ${b.from}–${b.to}`}</dd>
          {b.location ? (
            <>
              <dt>{c.where}</dt>
              <dd>{b.location}</dd>
            </>
          ) : null}
          {b.crew.length ? (
            <>
              <dt>{c.crew}</dt>
              <dd>{b.crew.join(', ')}</dd>
            </>
          ) : null}
          {b.contact ? (
            <>
              <dt>{c.contact}</dt>
              <dd>
                {b.contact.name}
                {b.contact.phone ? (
                  <>
                    {' · '}
                    <a href={`tel:${b.contact.phone.replace(/[^+\d]/g, '')}`}>{b.contact.phone}</a>
                  </>
                ) : null}
              </dd>
            </>
          ) : null}
          {b.plan ? (
            <>
              <dt>{c.plan}</dt>
              <dd>{b.plan}</dd>
            </>
          ) : null}
        </dl>
        {b.invite ? <p class="small muted" style="margin:0">{c.inGoogle}</p> : null}
        {it.projectId ? (
          <div class="cluster">
            <a class="btn btn-sm" href={`/projects/${it.projectId}/shoots`} onClick={goTo(it.projectId, `/projects/${it.projectId}/shoots`)}>
              {c.openProject}
            </a>
          </div>
        ) : null}
      </>
    );
  }
  return (
    <div class="wkc-card" role="dialog" aria-label={'title' in it ? it.title : c.google} tabIndex={-1} ref={ref} style={`top:${pos.top}px;left:${pos.left}px`}>
      <button type="button" class="btn btn-sm btn-ghost wkc-card-x" aria-label={c.close} onClick={p.onClose}>
        ✕
      </button>
      {body}
    </div>
  );
}

/**
 * The week at the top of My Work (Del 1 UX-1003): planned editing, shoots, deadlines, absence and busy time from
 * Google, Monday to Friday (weekend days only when something is on them). Under 720 px: day chips and one day's agenda.
 */
/** `fold`: on My work the week folds up under its heading, which then says what today holds (owner 2026-11-02). */
export function WeekCalendar(p: { api: Api; admin?: boolean; openProject?: (projectId: string) => void; fold?: boolean }) {
  const [open, toggle] = useFold('week', true);
  const lang = useLang();
  const t = DICT[lang];
  const tw = t.week;
  const [week, setWeek] = useState<string | null>(null);
  const [v, setV] = useState<MyWeekView | null | 'off'>(() => peek(p.api, '/api/my-week') ?? null);
  const [all, setAll] = useState(false);
  const [sel, setSel] = useState<string | null>(() => (v && v !== 'off' ? (v.days.find((d) => d.today)?.date ?? v.days[0]?.date ?? null) : null));
  const [card, setCard] = useState<{ it: WeekItem; date: string; at: DOMRect; key: string } | null>(null);
  const [rev, setRev] = useState(0);
  useEffect(() => onPlanChanged(() => setRev((n) => n + 1)), []);
  useEffect(() => {
    let live = true;
    setCard(null);
    p.api.get<MyWeekView>(`/api/my-week${week ? `?week=${week}` : ''}`).then(
      (x) => live && (setV(x), setSel(x.days.find((d) => d.today)?.date ?? x.days[0]?.date ?? null)),
      () => live && setV((cur) => (cur && cur !== 'off' ? cur : 'off'))
    );
    return () => {
      live = false;
    };
  }, [week, rev]);
  if (v === 'off') return null;
  if (!v) return <div class="panel wkc wkc-skel" aria-busy="true" aria-label={tw.loading} />;
  const more = Math.max(0, ...v.days.map((d) => d.items.length - FOLD));
  const planned = plannedLine(v.planned, lang);
  const folded = !!p.fold && !open;
  const today = v.days.find((d) => d.today);
  const nav = (
        <div class="wkc-nav">
          <button type="button" class="btn btn-sm btn-ghost" aria-label={tw.prev} onClick={() => setWeek(addDays(v.week, -7))}>
            ‹
          </button>
          <button type="button" class="btn btn-sm" disabled={v.week === v.thisWeek} onClick={() => setWeek(v.thisWeek)}>
            {t.common.today}
          </button>
          <button type="button" class="btn btn-sm btn-ghost" aria-label={tw.next} onClick={() => setWeek(addDays(v.week, 7))}>
            ›
          </button>
        </div>
  );
  return (
    <OpenProject.Provider value={p.openProject ?? null}>
    <section class="panel wkc" aria-labelledby="wkc-h" data-tour="week">
      {p.fold ? (
        <FoldHead id="wkc-h" title={weekTitle(v, lang)} hint={[folded && today ? tw.nToday(today.items.length) : '', planned ?? ''].filter(Boolean).join(' · ')} open={open} toggle={toggle}>
          {open ? nav : null}
        </FoldHead>
      ) : (
        <header class="wkc-head">
          <div class="wkc-title">
            <h2 class="blk-title" id="wkc-h">
              {weekTitle(v, lang)}
            </h2>
            {planned ? <span class="small muted">{planned}</span> : null}
          </div>
          {nav}
        </header>
      )}
      {folded ? null : (
      <div id="wkc-h-body">
      <div class="wkc-chips" role="tablist" aria-label={tw.days}>
        {v.days.map((d) => (
          <button type="button" role="tab" aria-selected={d.date === sel} class={`wkc-chip${d.today ? ' is-today' : ''}`} onClick={() => setSel(d.date)}>
            <span>{tw.dow[dt(d.date).getUTCDay()]}</span>
            <b>{dayNo(d.date)}</b>
            <span class="wkc-chip-h">{d.plannedH ? h(d.plannedH) : d.items.length ? '•' : '–'}</span>
          </button>
        ))}
      </div>
      <div class="wkc-grid" style={`--wkc-n:${v.days.length}`}>
        {v.days.map((d) => {
          const shown = all ? d.items : d.items.slice(0, FOLD);
          return (
            <div class={`wkc-day${d.today ? ' is-today' : ''}${d.closed ? ' is-closed' : ''}${d.date === sel ? ' is-sel' : ''}`} aria-label={`${tw.dow[dt(d.date).getUTCDay()]} ${dayNo(d.date)}`}>
              <div class="wkc-day-h">
                <span class="wkc-dow">
                  {tw.dow[dt(d.date).getUTCDay()]} <b>{dayNo(d.date)}</b>
                  {d.today ? <span class="sr-only">{tw.todayMark}</span> : null}
                </span>
                {d.plannedH ? (
                  <span class="wkc-h" title={tw.planned}>
                    {h(d.plannedH)}
                  </span>
                ) : null}
              </div>
              {d.holiday ? <p class="wkc-note">{tw.holiday}</p> : null}
              {d.items.length ? (
                <ul class="wkc-items">
                  {shown.map((it, i) => {
                    const key = `${d.date}|${i}`;
                    if (it.kind === 'plan') return <PlanItem it={it} date={d.date} api={p.api} />;
                    return <Item it={it} open={card?.key === key} onOpen={(el) => setCard(card?.key === key ? null : { it, date: d.date, at: el.getBoundingClientRect(), key })} />;
                  })}
                </ul>
              ) : d.holiday ? null : (
                <p class="wkc-note">{tw.nothing}</p>
              )}
              {!all && d.items.length > FOLD ? (
                <button type="button" class="wkc-more" onClick={() => setAll(true)}>
                  {tw.more(d.items.length - FOLD)}
                </button>
              ) : null}
              {/* "+ Planera" (owner 2026-11-03): a block of your own time on one of your projects, from today on */}
              {v.plan?.on && d.date >= v.today ? (
                <button type="button" class="wkc-add" aria-label={CT[lang].planOn(`${tw.dow[dt(d.date).getUTCDay()]} ${dayNo(d.date)}`)} onClick={() => openPlan({ day: d.date })}>
                  {CT[lang].addPlan}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
      {all && more ? (
        <button type="button" class="wkc-more wkc-less" onClick={() => setAll(false)}>
          {tw.less}
        </button>
      ) : null}
      {card ? <EventCard it={card.it} date={card.date} at={card.at} onClose={() => setCard(null)} /> : null}
      {v.calendar === 'error' ? <p class="wkc-foot small muted">{tw.calError}</p> : null}
      {v.calendar === 'off' && p.admin ? <p class="wkc-foot small muted">{tw.calOff}</p> : null}
      </div>
      )}
    </section>
    </OpenProject.Provider>
  );
}
